/**
 * "I'm standing here — do these doors next."
 *
 * A canvasser circles the block they are on with the map lasso, and those
 * stops move to the front of their working order. That is the whole feature,
 * and it is deliberately smaller than it sounds:
 *
 * ⚠️ CLIENT-SIDE ONLY. This reorders a local array. It does NOT write a route,
 * does not touch `route_stops.sequence`, and calls no endpoint. Editing routes
 * is COORDINATOR_PLUS (`ROUTE_WRITE_ROLES`) and CANVASSER is excluded on
 * purpose; a canvasser resequencing their own assignment server-side would be
 * exactly the menu-vs-endpoint gap we have spent this cycle closing. Every stop
 * here is one they were already assigned — this is a view over it, nothing more.
 * The pins keep their `sequence` label for the same reason: the route's numbers
 * still mean what the coordinator meant by them.
 *
 * Geometry mirrors the server's approach in `POST /voters/within` (see
 * memory-bank/plans/lasso-route-selection.md): bounding-box prefilter, then an
 * exact ray-cast refine. No geometry dependency. At route scale (30-150 stops
 * against a ≤501-vertex ring) the cost is irrelevant, but mirroring the server
 * keeps one mental model of what "inside the lasso" means across the app.
 */

export interface RingPoint {
  lat: number;
  lng: number;
}

export interface RingBBox {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

/** Minimum vertices for a ring to enclose anything. */
const MIN_RING_POINTS = 3;

export function ringBBox(ring: RingPoint[]): RingBBox | null {
  if (ring.length < MIN_RING_POINTS) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of ring) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return { minLat, maxLat, minLng, maxLng };
}

/**
 * Even-odd ray cast in raw lat/lng space, treating longitude as x.
 *
 * Planar, not spherical — the same simplification the server makes. A lasso is
 * a few blocks wide and never crosses the antimeridian, so the projection error
 * is far below the precision of a residential geocode.
 *
 * Pass `bbox` when testing many points against one ring; it is the prefilter,
 * and recomputing it per point is the one way to make this slow.
 */
export function pointInRing(
  lat: number,
  lng: number,
  ring: RingPoint[],
  bbox: RingBBox | null = ringBBox(ring),
): boolean {
  if (!bbox || ring.length < MIN_RING_POINTS) return false;
  if (lat < bbox.minLat || lat > bbox.maxLat) return false;
  if (lng < bbox.minLng || lng > bbox.maxLng) return false;

  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const yi = ring[i].lat;
    const xi = ring[i].lng;
    const yj = ring[j].lat;
    const xj = ring[j].lng;
    // Half-open edge test (yi > lat) !== (yj > lat): a vertex exactly at the
    // ray's latitude is counted once, not twice, so a stop level with a corner
    // of the lasso doesn't flip to "outside".
    const straddles = yi > lat !== yj > lat;
    if (!straddles) continue;
    const xCross = ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (lng < xCross) inside = !inside;
  }
  return inside;
}

/** The shape a route stop has to have to be lassoable. */
export interface LassoableStop {
  id: number;
  voter: { lat?: number | null; lng?: number | null };
}

/**
 * Which stops the ring caught, as a Set of stop ids.
 *
 * Two exclusions, both deliberate:
 *
 * 1. **Already-knocked stops never join the selection.** The selection means
 *    "do these next", and a door that is done is not a door to do. Including
 *    them would inflate the "N pulled forward" count with work already
 *    finished and shove knocked rows back to the top of the list, which is
 *    precisely the noise the canvasser is trying to get away from. They stay
 *    where they are, still striped "Knocked".
 *
 * 2. **Stops with no coordinates can never be inside a polygon** — the same
 *    rule the server applies (`/voters/within` requires non-null lat/lng), and
 *    `(0, 0)` is rejected as the geocoder's "did not resolve" sentinel rather
 *    than trusted as a point in the Atlantic. What matters is what happens to
 *    them next: see `orderStopsByLasso`, they keep their place in the list. An
 *    ungeocoded stop that silently vanished from the working order would be a
 *    real door that never gets knocked, which is the failure mode the lasso
 *    plan calls out by name.
 */
export function selectStopsInRing<T extends LassoableStop>(
  stops: T[],
  ring: RingPoint[],
  knockedStopIds: Set<number> = new Set(),
): Set<number> {
  const bbox = ringBBox(ring);
  const hits = new Set<number>();
  if (!bbox) return hits;
  for (const s of stops) {
    if (knockedStopIds.has(s.id)) continue;
    const { lat, lng } = s.voter || {};
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    if (lat === 0 && lng === 0) continue;
    if (pointInRing(lat, lng, ring, bbox)) hits.add(s.id);
  }
  return hits;
}

/**
 * The working order: selected stops first, everything else untouched behind
 * them, both groups keeping their original relative order.
 *
 * A stable partition rather than a sort, so the route's own sequence still
 * governs inside each group — the canvasser gets "these six, in route order,
 * then the rest of the route as planned", not a shuffled list. Stops without
 * coordinates simply never land in the first group, so they ride along in the
 * second one and stay visible and knockable. Clearing the lasso is just
 * calling this with an empty set, which returns the original order exactly —
 * that is what makes the undo trustworthy.
 */
export function orderStopsByLasso<T extends { id: number }>(
  stops: T[],
  selectedIds: Set<number>,
): T[] {
  if (selectedIds.size === 0) return stops;
  const front: T[] = [];
  const rest: T[] = [];
  for (const s of stops) (selectedIds.has(s.id) ? front : rest).push(s);
  return front.concat(rest);
}
